import { useCallback, useMemo } from "react";
import type { LodgingMediaAsset } from "@b2bportal/lodging-api";
import { useI18nContext } from "@hopper-b2b/i18n";
import { useControlledDialog } from "@tenant/ui";
import type { ImageGalleryComponentProps } from "./ImageGallery";

export enum MinimumImage {
  one = "minimum-image-one",
  two = "minimum-image-two",
  three = "minimum-image-three",
  five = "minimum-image-five",
}

export const useImageGallery = ({
  name,
  media,
}: ImageGalleryComponentProps) => {
  const { t } = useI18nContext();

  const imageGalleryDialog = useControlledDialog();

  const imageColumns: MinimumImage | null = useMemo(() => {
    const length = media.length;

    if (length === 1) return MinimumImage.one;
    else if (length === 2) return MinimumImage.two;
    else if (length === 3) return MinimumImage.three;
    else if (length >= 5) return MinimumImage.five;
    else return null;
  }, [media.length]);

  // Show 5 images - CSS will handle showing only 1 on mobile
  const showCaseImages = useMemo(() => {
    return media.slice(0, 5);
  }, [media]);

  const getAltText = useCallback(
    (image: LodgingMediaAsset) => {
      const category =
        image != null && "category" in image ? image.category : undefined;
      return category != null
        ? t("core-hotels.image.altWithCategory", {
            category: category,
          })
        : t("core-hotels.image.altWithNoCategory");
    },
    [t]
  );

  return useMemo(() => {
    return {
      context: {
        imageColumns,
        showCaseImages,
        images: media,
        imageGalleryDialog,
      },
      text: {
        dialogTitle: t("core-hotels.image.imageGallery.title", {
          hotelName: name,
        }),
        getAltText,
        imageButton: t("core-hotels.image.showAllImages"),
        chooseRoom: t("core-hotels.image.imageGallery.chooseRoom"),
      },
    };
  }, [
    imageColumns,
    showCaseImages,
    media,
    imageGalleryDialog,
    t,
    name,
    getAltText,
  ]);
};
